import {
  type PropertyDef,
  useCreateProperty,
  useDeleteProperty,
  useProperties,
  useUpdateProperty,
} from "@/hooks/useProperties";
import {
  Button,
  ConfirmDialog,
  FormDialog,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  sonnerToast,
} from "@simplicity/ui";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

const TYPES = [
  { value: "single-select", label: "Single select" },
  { value: "multi-select", label: "Multi select" },
];

function typeLabel(t: string): string {
  return TYPES.find((entry) => entry.value === t)?.label ?? t;
}

type DialogState = { mode: "create" } | { mode: "edit"; editing: PropertyDef } | null;

function PropertyRow({
  property,
  onEdit,
  onDelete,
}: {
  property: PropertyDef;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="flex items-center justify-between px-4 py-3 text-sm">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="font-medium">{property.label}</span>
          <span className="text-xs text-muted-foreground">({property.key})</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {typeLabel(property.type)} · {property.options.length} option
          {property.options.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-auto w-auto p-1 text-muted-foreground hover:text-primary"
          onClick={onEdit}
          title={`Edit ${property.label}`}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-auto w-auto p-1 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
          title={`Delete ${property.label}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

function OptionsEditor({
  options,
  onChange,
}: {
  options: string[];
  onChange: (next: string[]) => void;
}) {
  const setAt = (i: number, val: string) => {
    const next = [...options];
    next[i] = val;
    onChange(next);
  };
  const removeAt = (i: number) => onChange(options.filter((_, idx) => idx !== i));
  const append = () => onChange([...options, ""]);

  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-foreground">Options</span>
      <ul className="space-y-1.5">
        {options.map((opt, i) => (
          <li key={i} className="flex items-center gap-2">
            <Input
              value={opt}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder="Option label"
              className="h-8"
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-1 text-muted-foreground hover:text-destructive"
              onClick={() => removeAt(i)}
              title="Remove option"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>
      <Button variant="outline" size="sm" onClick={append}>
        <Plus className="h-4 w-4" /> Add option
      </Button>
    </div>
  );
}

function PropertyEditorFields({
  isEdit,
  keyValue,
  setKeyValue,
  label,
  setLabel,
  type,
  setType,
  options,
  setOptions,
}: {
  isEdit: boolean;
  keyValue: string;
  setKeyValue: (v: string) => void;
  label: string;
  setLabel: (v: string) => void;
  type: string;
  setType: (v: string) => void;
  options: string[];
  setOptions: (v: string[]) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs font-medium text-foreground" htmlFor="prop-label">
          Label
        </Label>
        <Input
          id="prop-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. School"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- inside PropertyEditorDialog (FormDialog); focus label field on open
          autoFocus
        />
      </div>
      {!isEdit && (
        <div className="space-y-1">
          <Label className="text-xs font-medium text-foreground" htmlFor="prop-key">
            Key
          </Label>
          <Input
            id="prop-key"
            value={keyValue}
            onChange={(e) => setKeyValue(e.target.value)}
            placeholder="kebab-case, e.g. school or grade-band"
          />
          <span className="text-xs text-muted-foreground">
            Lowercase letters, numbers, hyphens or underscores. Used in URLs and storage.
          </span>
        </div>
      )}
      {!isEdit && (
        <div className="space-y-1">
          <span className="text-xs font-medium text-foreground">Type</span>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((entry) => (
                <SelectItem key={entry.value} value={entry.value}>
                  {entry.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <OptionsEditor options={options} onChange={setOptions} />
    </div>
  );
}

const EDIT_LABELS = {
  title: "Edit Property",
  description:
    "Rename the label or modify its options. Renaming an option updates every dashboard already tagged with it.",
  submitButton: "Save",
  errorTitle: "Failed to update property",
} as const;

const CREATE_LABELS = {
  title: "Add Property",
  description:
    "Define a new property that dashboards in this tenant can be tagged with. Key is the stable machine name and cannot be changed later.",
  submitButton: "Create",
  errorTitle: "Failed to create property",
} as const;

async function submitProperty(args: {
  isEdit: boolean;
  initial: PropertyDef | undefined;
  label: string;
  key: string;
  type: string;
  options: string[];
  createMut: {
    mutateAsync: (input: {
      key: string;
      label: string;
      type: string;
      options: string[];
    }) => Promise<unknown>;
  };
  updateMut: {
    mutateAsync: (input: { id: string; label?: string; options?: string[] }) => Promise<unknown>;
  };
}) {
  if (args.isEdit && args.initial) {
    await args.updateMut.mutateAsync({
      id: args.initial.id,
      label: args.label,
      options: args.options,
    });
  } else {
    await args.createMut.mutateAsync({
      key: args.key,
      label: args.label,
      type: args.type,
      options: args.options,
    });
  }
}

type SubmitArgs = Parameters<typeof submitProperty>[0];

async function commitEditorSave(args: SubmitArgs & { errorTitle: string; onClose: () => void }) {
  try {
    await submitProperty(args);
    args.onClose();
  } catch (err) {
    sonnerToast.error(args.errorTitle, {
      description: err instanceof Error ? err.message : undefined,
    });
  }
}

function useEditorFormState(initial: PropertyDef | undefined) {
  const [keyValue, setKeyValue] = useState(initial?.key ?? "");
  const [label, setLabel] = useState(initial?.label ?? "");
  const [type, setType] = useState(initial?.type ?? "multi-select");
  const [options, setOptions] = useState<string[]>(initial?.options ?? []);
  return { keyValue, setKeyValue, label, setLabel, type, setType, options, setOptions };
}

/**
 * Owns the dialog's mutable state + commit logic so `PropertyEditorDialog`'s
 * body stays under the complexity cap. The parent wraps the dialog in a
 * `key` swap so this hook runs fresh on every open.
 */
function usePropertyEditor(state: Exclude<DialogState, null>, onClose: () => void) {
  const createMut = useCreateProperty();
  const updateMut = useUpdateProperty();

  const isEdit = state.mode === "edit";
  const initial = isEdit ? state.editing : undefined;
  const labels = isEdit ? EDIT_LABELS : CREATE_LABELS;

  const form = useEditorFormState(initial);

  const trimmedOptions = form.options.map((o) => o.trim()).filter((o) => o.length > 0);
  const labelTrimmed = form.label.trim();
  const keyTrimmed = form.keyValue.trim();
  const canSave = labelTrimmed.length > 0 && (isEdit || keyTrimmed.length > 0);

  const handleSave = () => {
    if (!canSave) return;
    void commitEditorSave({
      isEdit,
      initial,
      label: labelTrimmed,
      key: keyTrimmed,
      type: form.type,
      options: trimmedOptions,
      createMut,
      updateMut,
      errorTitle: labels.errorTitle,
      onClose,
    });
  };

  return { isEdit, labels, canSave, form, handleSave };
}

function PropertyEditorDialog({
  state,
  onClose,
}: {
  // Always non-null here — parent wraps this in a `key` swap so a fresh
  // instance mounts whenever the dialog opens or the edited property changes.
  state: Exclude<DialogState, null>;
  onClose: () => void;
}) {
  const editor = usePropertyEditor(state, onClose);
  return (
    <FormDialog
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={editor.labels.title}
      description={editor.labels.description}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={editor.handleSave} disabled={!editor.canSave}>
            {editor.labels.submitButton}
          </Button>
        </>
      }
    >
      <PropertyEditorFields
        isEdit={editor.isEdit}
        keyValue={editor.form.keyValue}
        setKeyValue={editor.form.setKeyValue}
        label={editor.form.label}
        setLabel={editor.form.setLabel}
        type={editor.form.type}
        setType={editor.form.setType}
        options={editor.form.options}
        setOptions={editor.form.setOptions}
      />
    </FormDialog>
  );
}

export function PropertyManagement() {
  const { data: properties, isLoading } = useProperties();
  const deleteMut = useDeleteProperty();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [confirmDelete, setConfirmDelete] = useState<PropertyDef | null>(null);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteMut.mutateAsync(confirmDelete.id);
      setConfirmDelete(null);
    } catch (err) {
      sonnerToast.error("Failed to delete property", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  // `key` forces a fresh mount (and fresh useState seeds) every time the
  // dialog opens or the edited property changes. Cleaner than threading a
  // re-seed effect through useState.
  const dialogKey =
    dialog?.mode === "edit"
      ? `edit-${dialog.editing.id}`
      : dialog?.mode === "create"
        ? "create"
        : null;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Properties</h2>
          <p className="text-sm text-muted-foreground">
            Define metadata fields that dashboards in this tenant can be tagged with — for example,
            School or Grade Band. Slice 1: definitions only; tagging from the dashboard list ships
            next.
          </p>
        </div>
        <Button size="sm" onClick={() => setDialog({ mode: "create" })}>
          <Plus className="h-4 w-4" /> Add Property
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : properties.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No properties defined yet. Click <em>Add Property</em> to create one.
        </p>
      ) : (
        <ul className="border border-border rounded divide-y divide-border">
          {properties.map((p) => (
            <PropertyRow
              key={p.id}
              property={p}
              onEdit={() => setDialog({ mode: "edit", editing: p })}
              onDelete={() => setConfirmDelete(p)}
            />
          ))}
        </ul>
      )}

      {dialog !== null && dialogKey !== null && (
        <PropertyEditorDialog key={dialogKey} state={dialog} onClose={() => setDialog(null)} />
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(null);
        }}
        title="Delete Property"
        description={
          confirmDelete
            ? `Are you sure you want to delete "${confirmDelete.label}"? All dashboard tags using this property will also be removed. This action cannot be undone.`
            : ""
        }
        onConfirm={() => {
          void handleDelete();
        }}
        options={{ destructive: true, confirmLabel: "Delete" }}
      />
    </section>
  );
}
